import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  CircularProgress,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showDisabled, setShowDisabled] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await apiService.getUsers();
        setUsers(data);
        setFilteredUsers(data.filter(user => user.is_enabled || showDisabled));
      } catch (err) {
        console.error('Error al cargar usuarios:', err);
        setError('Error al cargar los usuarios. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [showDisabled]);

  useEffect(() => {
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = users.filter(
        user =>
          (user.first_name.toLowerCase().includes(lowercasedSearch) ||
          user.father_last_name.toLowerCase().includes(lowercasedSearch) ||
          user.mother_last_name.toLowerCase().includes(lowercasedSearch) ||
          user.email.toLowerCase().includes(lowercasedSearch) ||
          user.document_number.includes(searchTerm)) &&
          (user.is_enabled || showDisabled)
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users.filter(user => user.is_enabled || showDisabled));
    }
    setPage(0);
  }, [searchTerm, users, showDisabled]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleCreateUser = () => {
    navigate('/users/create');
  };

  const handleEditUser = (id) => {
    navigate(`/users/edit/${id}`);
  };

  const handleViewUser = (id) => {
    navigate(`/users/view/${id}`);
  };

  const handleToggleStatus = async (id, isEnabled) => {
    try {
      if (isEnabled) {
        await apiService.disableUser(id);
      } else {
        await apiService.enableUser(id);
      }
      
      // Actualizar la lista de usuarios
      const updatedUsers = users.map(user => {
        if (user.id === id) {
          return { ...user, is_enabled: !isEnabled };
        }
        return user;
      });
      
      setUsers(updatedUsers);
    } catch (err) {
      console.error('Error al cambiar estado del usuario:', err);
      setError('Error al cambiar el estado del usuario. Por favor, intente nuevamente.');
    }
  };

  const getDocumentTypeLabel = (type) => {
    switch (type) {
      case 'DNI':
        return 'DNI';
      case 'CE':
        return 'C.E.';
      case 'PAS':
        return 'Pasaporte';
      default:
        return type;
    }
  };

  const getFullName = (user) => {
    return `${user.first_name} ${user.father_last_name} ${user.mother_last_name}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Usuarios</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          Nuevo Usuario
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <TextField
            variant="outlined"
            placeholder="Buscar por nombre, apellido, email o documento..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: '70%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box display="flex" alignItems="center">
            <Switch
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              color="primary"
            />
            <Typography variant="body2">Mostrar deshabilitados</Typography>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Documento</TableCell>
              <TableCell>Nombre Completo</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No se encontraron usuarios
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Chip 
                        label={`${getDocumentTypeLabel(user.document_type)}: ${user.document_number}`} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>{getFullName(user)}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        {user.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_enabled ? 'Activo' : 'Inactivo'}
                        color={user.is_enabled ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalles">
                        <IconButton
                          color="info"
                          onClick={() => handleViewUser(user.id)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          color="primary"
                          onClick={() => handleEditUser(user.id)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.is_enabled ? 'Deshabilitar' : 'Habilitar'}>
                        <Switch
                          checked={user.is_enabled}
                          onChange={() => handleToggleStatus(user.id, user.is_enabled)}
                          color="primary"
                          size="small"
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
    </Box>
  );
};

export default UserList; 